import axios from 'axios';

export const getApiBaseUrl = () => {
  // If running inside Capacitor Native Android / iOS, route to production backend
  if (
    typeof window !== 'undefined' &&
    (window.Capacitor?.isNativePlatform?.() || window.location.protocol === 'capacitor:')
  ) {
    return 'https://voicetodo-api.onrender.com';
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const hostname = window.location.hostname;
    // Localhost dev server
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000';
    }
    // LAN IP dev e.g. 192.168.x.x
    if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return `http://${hostname}:5000`;
    }
    // Any other domain (e.g. hs-todo.pages.dev, voicetodo.app) -> ALWAYS production Render backend
    return 'https://voicetodo-api.onrender.com';
  }

  if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
    return process.env.REACT_APP_API_URL;
  }

  // Production fallback for build-time or non-localhost
  return 'https://voicetodo-api.onrender.com';
};

export const API_BASE_URL = getApiBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds to accommodate Render.com cold starts
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to requests (support both auth_token and token keys) and enforce dynamic baseURL
apiClient.interceptors.request.use((config) => {
  // Dynamically set baseURL on every request based on current runtime host
  config.baseURL = getApiBaseUrl();

  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Silent health ping to wake up sleeping Render free-tier instance on page mount
export const pingHealth = async () => {
  try {
    await apiClient.get('/health', { timeout: 15000 });
  } catch (e) {
    // Ignore silent ping errors
  }
};

// Handle 401 responses, network timeouts, and cold-start retries
apiClient.interceptors.response.use(
  (response) => {
    const refreshed = response.headers?.['x-refreshed-token'];
    if (refreshed) {
      try {
        localStorage.setItem('auth_token', refreshed);
        localStorage.setItem('token', refreshed);
      } catch (_) {}
    }
    return response;
  },
  async (error) => {
    const config = error.config;

    // Handle 401 Unauthorized with automatic transparent re-auth recovery
    if (error.response?.status === 401 && config && !config._is401Retry) {
      config._is401Retry = true;
      try {
        const storedUser = localStorage.getItem('auth_user') || localStorage.getItem('user');
        let userEmail = 'google.user@voicetodo.app';
        let googleId = 'google_demo_id';
        let userName = 'Google User';
        if (storedUser) {
          try {
            const userObj = JSON.parse(storedUser);
            if (userObj?.email) userEmail = userObj.email;
            if (userObj?.google_id || userObj?.id) googleId = userObj.google_id || userObj.id;
            if (userObj?.name) userName = userObj.name;
          } catch (_) {}
        }
        console.log('🔄 401 recovery: refreshing session via /auth/google for', userEmail);
        const refreshRes = await axios.post(`${getApiBaseUrl()}/auth/google`, {
          email: userEmail,
          google_id: googleId,
          name: userName,
        }, { timeout: 30000 });
        if (refreshRes?.data?.token) {
          const freshToken = refreshRes.data.token;
          localStorage.setItem('auth_token', freshToken);
          localStorage.setItem('token', freshToken);
          config.headers.Authorization = `Bearer ${freshToken}`;
          return apiClient(config);
        }
      } catch (refreshErr) {
        console.warn("401 session recovery failed:", refreshErr.message);
      }
      return Promise.reject(error);
    }

    // Auto-retry on network error / timeout / 502 / 503 / 504 (up to 2 retries)
    const isNetworkOrColdStart =
      !error.response ||
      error.code === 'ECONNABORTED' ||
      [502, 503, 504].includes(error.response?.status);

    if (isNetworkOrColdStart && config && (!config._retryCount || config._retryCount < 2)) {
      config._retryCount = (config._retryCount || 0) + 1;
      // Trigger background health ping to accelerate Render wake-up
      pingHealth();
      // Wait 1.5 seconds before retrying
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return apiClient(config);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
